import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCompanySelectionGate } from '../CompanySelectionGateContext';
import {
  selectConfigByInstance,
  selectDatasetInstances,
  selectDeliverableClient,
  selectDeliverableName,
  selectModelVersions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  buildDatasetInstanceLabelMap,
  buildDatasetLabelMap,
} from '../datasets/dataset-instance-labels';
import {
  estimateDataBuilderPayload,
  fetchDataBuilderResults,
} from './data-builder-api';
import type { DataBuilderPayload } from './data-builder-api.types';
import type {
  RowLimitInstanceResult,
  RowLimitResultsByInstanceId,
} from './data-builder-row-limit.types';
import { extractDataBuilderRowLimitErrorMessage } from './data-builder-row-limit-errors';
import { parseValidateRowLimitResponse } from './parse-validate-row-limit-response';
import { getReadyInstanceIdsForRowLimit } from './row-limit-readiness';
import { useBuildDataBuilderPayload } from './useBuildDataBuilderPayload';
import { useDataBuilderQuery } from './useDataBuilderQuery';

const FALLBACK_NAME = 'Validating Row Limit';
const ROW_LIMIT_VALIDATION_DEBOUNCE_MS = 400;

type UseDataBuilderRowLimitResult = {
  resultsByInstanceId: RowLimitResultsByInstanceId;
  hasOverLimit: boolean;
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  isReady: boolean;
  isWaitingForCompanies: boolean;
};

type MutationVariables = {
  payload: DataBuilderPayload;
  nameToInstanceId: Record<string, string>;
  payloadSignature: string;
  requestedInstanceIds: string[];
};
type MutationResult = {
  raw: unknown;
  nameToInstanceId: Record<string, string>;
  payloadSignature: string;
  requestedInstanceIds: string[];
};

export const useDataBuilderRowLimit = (): UseDataBuilderRowLimitResult => {
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });

  const { status: companySelectionGateStatus } = useCompanySelectionGate();
  const datasetInstances = useDataBuilderSelector(selectDatasetInstances);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);

  const trimmedName = storedName?.trim() ?? '';
  const payloadName = trimmedName.length > 0 ? trimmedName : FALLBACK_NAME;
  const payloadClient = storedClient !== '' ? storedClient : null;

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
    rowLimitValidationProbe: true,
  });

  const labelMap = useMemo(
    () => buildDatasetLabelMap(data?.datasets),
    [data?.datasets]
  );
  const instanceLabelMap = useMemo(
    () => buildDatasetInstanceLabelMap(datasetInstances, labelMap),
    [datasetInstances, labelMap]
  );

  const readyInstanceIds = useMemo(
    () =>
      getReadyInstanceIdsForRowLimit({
        datasetInstances,
        configByInstance,
        deliverablesConfig: data?.deliverablesConfig ?? {},
        modelVersions,
        instanceLabelById: instanceLabelMap,
      }),
    [
      configByInstance,
      data?.deliverablesConfig,
      datasetInstances,
      instanceLabelMap,
      modelVersions,
    ]
  );
  const readyInstanceIdList = useMemo(
    () => Array.from(readyInstanceIds).sort(),
    [readyInstanceIds]
  );

  const { mutate, reset, isPending, isError, error } = useMutation<
    MutationResult,
    Error,
    MutationVariables
  >({
    mutationFn: async ({
      payload,
      nameToInstanceId,
      payloadSignature,
      requestedInstanceIds,
    }) => {
      const raw = await estimateDataBuilderPayload(payload);
      return {
        raw,
        nameToInstanceId,
        payloadSignature,
        requestedInstanceIds,
      };
    },
  });

  const payloadResult = useMemo(() => {
    if (rawSchema === undefined || readyInstanceIds.size === 0) {
      return null;
    }
    return build(rawSchema, readyInstanceIds);
  }, [build, rawSchema, readyInstanceIds]);
  const payload = payloadResult?.payload ?? null;

  const nameToInstanceId = useMemo(() => {
    if (payloadResult === null) {
      return {};
    }
    const result: Record<string, string> = {};
    for (const [instanceId, deliverableName] of Object.entries(
      payloadResult.deliverableNameByInstanceId
    )) {
      if (deliverableName in result) {
        continue;
      }
      result[deliverableName] = instanceId;
    }
    return result;
  }, [payloadResult]);

  const canRunRowLimitProbe =
    payload !== null && payload.deliverables.length > 0;
  const payloadSignature = canRunRowLimitProbe ? JSON.stringify(payload) : null;
  const nameMapSignature = useMemo(
    () => JSON.stringify(nameToInstanceId),
    [nameToInstanceId]
  );
  const readyInstanceIdsSignature = useMemo(
    () => JSON.stringify(readyInstanceIdList),
    [readyInstanceIdList]
  );
  const [rawResultsByInstanceId, setRawResultsByInstanceId] =
    useState<RowLimitResultsByInstanceId>({});
  const lastValidatedPayloadSignatureByInstanceIdRef = useRef<
    Record<string, string>
  >({});

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (
      rawSchema === undefined ||
      companySelectionGateStatus === 'pending' ||
      payloadSignature === null ||
      payload === null
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (payload === null) {
        return;
      }
      mutate(
        {
          payload,
          nameToInstanceId: JSON.parse(nameMapSignature) as Record<
            string,
            string
          >,
          payloadSignature,
          requestedInstanceIds: JSON.parse(
            readyInstanceIdsSignature
          ) as string[],
        },
        {
          onSuccess: (response) => {
            const parsedResults = parseValidateRowLimitResponse(
              response.raw,
              response.nameToInstanceId
            );
            const signaturesForParsedResults = Object.fromEntries(
              Object.keys(parsedResults).map((instanceId) => [
                instanceId,
                response.payloadSignature,
              ])
            );
            const signaturesOutsideRequest = Object.fromEntries(
              Object.entries(
                lastValidatedPayloadSignatureByInstanceIdRef.current
              ).filter(
                ([instanceId]) =>
                  !response.requestedInstanceIds.includes(instanceId)
              )
            );
            lastValidatedPayloadSignatureByInstanceIdRef.current = {
              ...signaturesOutsideRequest,
              ...signaturesForParsedResults,
            };
            setRawResultsByInstanceId((prev) => {
              const next = Object.fromEntries(
                Object.entries(prev).filter(
                  ([instanceId]) =>
                    !response.requestedInstanceIds.includes(instanceId)
                )
              );
              return {
                ...next,
                ...parsedResults,
              };
            });
          },
        }
      );
    }, ROW_LIMIT_VALIDATION_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    payloadSignature,
    nameMapSignature,
    readyInstanceIdsSignature,
    companySelectionGateStatus,
    mutate,
    rawSchema,
  ]);

  useEffect(() => {
    if (payloadSignature !== null || !isError) {
      return;
    }
    reset();
  }, [isError, payloadSignature, reset]);

  const resultsByInstanceId = useMemo(() => {
    if (payloadSignature === null) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(rawResultsByInstanceId).filter(([instanceId]) => {
        const hasCurrentSignature =
          lastValidatedPayloadSignatureByInstanceIdRef.current[instanceId] ===
          payloadSignature;
        return readyInstanceIds.has(instanceId) && hasCurrentSignature;
      })
    );
  }, [payloadSignature, rawResultsByInstanceId, readyInstanceIds]);

  const hasOverLimit = Object.values(resultsByInstanceId).some(
    (result: RowLimitInstanceResult) => result.severity === 'error'
  );

  const errorMessage = extractDataBuilderRowLimitErrorMessage(error);

  const isWaitingForCompanies = companySelectionGateStatus === 'pending';
  const isReady =
    isWaitingForCompanies ||
    !canRunRowLimitProbe ||
    (!isPending && !isError);

  return {
    resultsByInstanceId,
    hasOverLimit,
    isPending,
    isError,
    errorMessage,
    isReady,
    isWaitingForCompanies,
  };
};
